import { FormikValues } from 'formik';
import noop from 'lodash/noop';
import { useCallback, useEffect, useState } from 'react';
import { useCookies } from 'react-cookie';
import { useCityStore } from 'src/store';

import { apiFront } from '@api/index';
import { DeliveryFormItem } from '@api/logistic';

import Form from '@controls/Form';

import Autocomplete from '@components/controls/Autocomplete';
import Popup from '@components/controls/Popup';

import { Layout, colors, scale, typography } from '@scripts/gds';
import { useMedia } from '@scripts/hooks/useMedia';

const cities = [
    'Москва',
    'Новосибирск',
    'Санкт-Петербург',
    'Нижний Новгород',
    'Воронеж',
    'Омск',
    'Волгоград',
    'Пермь',
    'Екатеринбург',
    'Ростов-на-Дону',
    'Казань',
    'Самара',
    'Краснодар',
    'Челябинск',
    'Красноярск',
    'Уфа',
];

const CityPopup = ({
    isOpen,
    closeHandler,
    handleChangeCity,
}: {
    isOpen: boolean;
    closeHandler: () => void;
    handleChangeCity?: (city?: DeliveryFormItem['address']) => void;
}) => {
    const { xs } = useMedia();
    const setCurrentCity = useCityStore(({ setCurrentCity }) => setCurrentCity);

    const [_, setCookies] = useCookies(['city']);

    const [suggestions, setSuggestions] = useState<
        { label: string; value: string; geo_lat: string; geo_lon: string }[]
    >([]);
    const [isEmpty, setIsEmpty] = useState(true);

    const changeCityHandler = useCallback(async (city: string) => {
        const res = await apiFront.post('addresses', { data: `г. ${city}` });

        const resWithStreet = await apiFront.post('city', {
            data: {
                latitude: res.data[0].data.geo_lat,
                longitude: res.data[0].data.geo_lon,
            },
        });

        const newCity = {
            city: resWithStreet.data.suggestions[0].data.city,
            city_guid: resWithStreet.data.suggestions[0].data.city_fias_id,
            city_kladr: resWithStreet.data.suggestions[0].data.city_kladr_id,
            country_code: resWithStreet.data.suggestions[0].data.country_iso_code,
            post_index: resWithStreet.data.suggestions[0].data.postal_code,
            region: resWithStreet.data.suggestions[0].data.region,
            region_guid: resWithStreet.data.suggestions[0].data.region_fias_id,
            geo_lat: resWithStreet.data.suggestions[0].data.geo_lat,
            geo_lon: resWithStreet.data.suggestions[0].data.geo_lon,
            address_string: resWithStreet.data.suggestions[0].value,
            street: resWithStreet.data.suggestions[0].data.street,
            house: resWithStreet.data.suggestions[0].data.house,
        };

        if (handleChangeCity) {
            handleChangeCity(newCity);
        }

        setCurrentCity(newCity);
        setCookies('city', newCity);

        closeHandler();
    }, []);

    const changeCityWithCoordsHandler = useCallback(async (latitude: string, longitude: string) => {
        const resWithStreet = await apiFront.post('city', {
            data: { latitude, longitude },
        });

        const newCity = {
            city: resWithStreet.data.suggestions[0].data.city,
            city_guid: resWithStreet.data.suggestions[0].data.city_fias_id,
            city_kladr: resWithStreet.data.suggestions[0].data.city_kladr_id,
            country_code: resWithStreet.data.suggestions[0].data.country_iso_code,
            post_index: resWithStreet.data.suggestions[0].data.postal_code,
            region: resWithStreet.data.suggestions[0].data.region,
            region_guid: resWithStreet.data.suggestions[0].data.region_fias_id,
            geo_lat: resWithStreet.data.suggestions[0].data.geo_lat,
            geo_lon: resWithStreet.data.suggestions[0].data.geo_lon,
            address_string: resWithStreet.data.suggestions[0].value,
            street: resWithStreet.data.suggestions[0].data.street,
            house: resWithStreet.data.suggestions[0].data.house,
        };

        if (handleChangeCity) {
            handleChangeCity(newCity);
        }

        setCurrentCity(newCity);
        setCookies('city', newCity);

        closeHandler();
    }, []);

    useEffect(() => {
        if (isOpen) {
            setSuggestions([]);
        }
    }, [isOpen]);

    return (
        <>
            <Popup
                borderWpaper={'popup-border-wrapper'}
                isOpen={isOpen}
                onRequestClose={closeHandler}
                css={{ height: '523px', width: '400px' }}
                isAuth
                isSwipeable
                scrollInside
                isNewStylePopupmobile
            >
                <Popup.Body>
                    <div
                        css={{
                            fontSize: '28px',
                            fontWeight: 500,
                            lineHeight: '33.6px',
                            marginBottom: '16px',
                            [xs]: {
                                textAlign: 'center',
                                fontSize: '18px',
                                lineHeight: '24px',
                            },
                        }}
                    >
                        Выберите город
                    </div>
                    <Form initialValues={{ city: '', rememberMe: false }} onSubmit={noop}>
                        {(): FormikValues => {
                            return (
                                <>
                                    <Layout cols={3} gap={scale(2, true)} css={{ ...typography('inputFields') }}>
                                        <Layout.Item col={3}>
                                            <Form.FastField name="search">
                                                <Autocomplete
                                                    newCustomError={
                                                        'Такого населенного пункта для доставки нет, выберите ближайший из доступных'
                                                    }
                                                    isHiddenSuggestions
                                                    theme="primary"
                                                    setIsEmpty={setIsEmpty}
                                                    searchAsyncFunc={async query => {
                                                        const res = await apiFront.post('addresses', {
                                                            data: query,
                                                        });

                                                        setSuggestions(
                                                            res.data
                                                                .slice(0, 7)
                                                                .map((suggestion: { value: string; data: any }) => ({
                                                                    label: suggestion.data.region_with_type.replace(
                                                                        /^(г|Г)+\s/,
                                                                        ''
                                                                    ),
                                                                    value: suggestion.value.replace(/^(г|Г)+\s/, ''),
                                                                    geo_lat: suggestion.data.geo_lat,
                                                                    geo_lon: suggestion.data.geo_lon,
                                                                }))
                                                        );

                                                        return (
                                                            res.data
                                                                .slice(0, 7)
                                                                .map((suggestion: { value: string; data: any }) => ({
                                                                    label: suggestion.data.region_with_type,
                                                                    value: suggestion.value,
                                                                    geo_lat: suggestion.data.geo_lat,
                                                                    geo_lon: suggestion.data.geo_lon,
                                                                })) || []
                                                        );
                                                    }}
                                                    placeholder="Ваш город"
                                                />
                                            </Form.FastField>
                                        </Layout.Item>
                                    </Layout>
                                    <Layout cols={2} css={{ marginTop: '24px' }}>
                                        {suggestions.length === 0 || isEmpty ? (
                                            <>
                                                {cities.map(item => (
                                                    <Layout.Item key={item}>
                                                        <button
                                                            css={{
                                                                color: colors.textSecondGrey,
                                                                ...typography('inputFields'),
                                                                textAlign: 'left',
                                                                fontWeight:
                                                                    item === 'Москва' || item === 'Санкт-Петербург'
                                                                        ? 500
                                                                        : 400,
                                                                lineHeight:
                                                                    item === 'Москва' || item === 'Санкт-Петербург'
                                                                        ? '25.6px'
                                                                        : '20px',
                                                            }}
                                                            onClick={() => changeCityHandler(item)}
                                                        >
                                                            {item}
                                                        </button>
                                                    </Layout.Item>
                                                ))}
                                            </>
                                        ) : (
                                            suggestions?.map((city, index) => (
                                                <Layout.Item col={2} key={`${city.value}-${index}`}>
                                                    <button
                                                        css={{
                                                            display: 'flex',
                                                            flexDirection: 'column',
                                                            gap: scale(1, true),
                                                            justifyContent: 'flex-start',
                                                        }}
                                                        onClick={() => {
                                                            if (city.geo_lat && city.geo_lon) {
                                                                changeCityWithCoordsHandler(city.geo_lat, city.geo_lon);
                                                            } else {
                                                                changeCityHandler(city.value);
                                                            }
                                                        }}
                                                    >
                                                        <div
                                                            css={{
                                                                ...typography('inputFields'),
                                                                textAlign: 'left',
                                                            }}
                                                        >
                                                            {city.value}
                                                        </div>
                                                        <div
                                                            css={{
                                                                color: colors.textSecondGrey,
                                                                textAlign: 'left',
                                                            }}
                                                        >
                                                            {city.label}
                                                        </div>
                                                    </button>
                                                </Layout.Item>
                                            ))
                                        )}
                                    </Layout>
                                </>
                            );
                        }}
                    </Form>
                </Popup.Body>
            </Popup>
        </>
    );
};

export default CityPopup;
